import React, { useCallback, useEffect, useMemo, useState, useRef, createContext, useContext } from 'react';
import { getProbability, getStatus, getStatusTheme, getCitation, getInterpretation, formatTimer, type Status, type StatusTheme } from './decay';
export type ClockSpeed = 1 | 2 | 5 | 10;
type LeadClockContextValue = {
  elapsed: number;
  probability: number;
  status: Status;
  theme: StatusTheme;
  citation: string;
  interpretation: string;
  timerLabel: string;
  speed: ClockSpeed;
  setSpeed: (s: ClockSpeed) => void;
  heroInView: boolean;
  setHeroInView: (v: boolean) => void;
  reset: () => void;
  resetPulse: number;
};
const LeadClockContext = createContext<LeadClockContextValue | null>(null);
// Fast-forward to ~56s on load so users land already feeling time pressure.
const FAST_TARGET_SECONDS = 50;
const FAST_DURATION_MS = 1800;
type Phase = 'spinUp' | 'ticking';
export function LeadClockProvider({
  children
}: {
  children: React.ReactNode;
}) {
  const [elapsed, setElapsed] = useState(0);
  const [speed, setSpeed] = useState<ClockSpeed>(2);
  const [heroInView, setHeroInView] = useState(true);
  const [resetPulse, setResetPulse] = useState(0);
  const [phase, setPhase] = useState<Phase>('spinUp');
  const rafRef = useRef<number | null>(null);
  // ---- SPIN-UP PHASE ----
  // Runs on mount and on reset (resetPulse change). Animates 0 → 56 over
  // ~1.8s, then flips to 'ticking'.
  useEffect(() => {
    if (phase !== 'spinUp') return;
    const prefersReducedMotion = typeof window !== 'undefined' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (prefersReducedMotion) {
      setElapsed(FAST_TARGET_SECONDS);
      setPhase('ticking');
      return;
    }
    const startTime = performance.now();
    const tick = (now: number) => {
      const dt = now - startTime;
      if (dt >= FAST_DURATION_MS) {
        rafRef.current = null;
        setElapsed(FAST_TARGET_SECONDS);
        setPhase('ticking');
        return;
      }
      const t = dt / FAST_DURATION_MS;
      const eased = 1 - Math.pow(1 - t, 2); // easeOutQuad
      setElapsed(Math.floor(eased * FAST_TARGET_SECONDS));
      rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);
    return () => {
      if (rafRef.current !== null) {
        cancelAnimationFrame(rafRef.current);
        rafRef.current = null;
      }
    };
  }, [phase, resetPulse]);
  // ---- TICKING PHASE ----
  // Adds 1 elapsed-second every (1000 / speed) ms. Re-created when speed
  // changes so the new rate applies immediately.
  useEffect(() => {
    if (phase !== 'ticking') return;
    const id = window.setInterval(() => {
      setElapsed(e => e + 1);
    }, 1000 / speed);
    return () => window.clearInterval(id);
  }, [phase, speed]);
  const reset = useCallback(() => {
    setElapsed(0);
    setPhase('spinUp');
    setResetPulse(p => p + 1);
  }, []);
  const value = useMemo<LeadClockContextValue>(() => {
    const probability = getProbability(elapsed);
    const status = getStatus(elapsed);
    return {
      elapsed,
      probability,
      status,
      theme: getStatusTheme(status),
      citation: getCitation(elapsed),
      interpretation: getInterpretation(status),
      timerLabel: formatTimer(elapsed),
      speed,
      setSpeed,
      heroInView,
      setHeroInView,
      reset,
      resetPulse
    };
  }, [elapsed, speed, heroInView, resetPulse, reset]);
  return <LeadClockContext.Provider value={value} data-id="element-352">
      {children}
    </LeadClockContext.Provider>;
}
export function useLeadClock() {
  const ctx = useContext(LeadClockContext);
  if (!ctx) {
    throw new Error('useLeadClock must be used inside <LeadClockProvider>');
  }
  return ctx;
}